/**
 * @author bly & lifei
 * 功能：生成路由表配置文件
 */

import { createRouter, createWebHistory, type RouteRecordRaw, type Router, type RouteMeta } from 'vue-router'
import { groupBy, isArray, omitBy, omit, pick, isString } from 'lodash-es'
import { pinyin as toPinyin } from 'pinyin-pro'
import { useStore } from '@/store'
import * as ENUM from '@/enum'
import { eventEmitter } from '../eventEmitter'
import * as func from '@/func'

type TUserInfo = TUser.Login['ApiGetInfo']['res']

type TMenuItem = TUserInfo['subMenu'][number] & {
  union?: NonNullable<RouteMeta['menus']>['union']
  authOpts?: Array<string>
  authFields?: Array<string>
}

const configFiles: TCommon.Json<TCommon.RouteConfig> = import.meta.glob('../../views/**/route.config.ts', {
  import: 'default',
  eager: true,
})

const vueFiles = import.meta.glob('../../views/**/*.vue', {
  import: 'default',
})

eventEmitter.on('API:401', () => {
  const { dispatch } = useStore()
  dispatch({ type: 'login/loginInfo', payload: null })
  window.location.replace('/login')
})

class SuperRouter {
  /**
   * 功能：路由配置文件
   */
  private readonly configFiles: TCommon.Json<TCommon.RouteConfig> = configFiles

  /**
   * 功能：路由视图文件
   */
  private readonly vueFiles = vueFiles

  /**
   * 功能：路由路径
   */
  private readonly routePaths: string[] = []

  /**
   * 功能：默认页面 404 或没有权限
   */
  private readonly defaultPage = '/defaultPage'

  /**
   * 功能：默认路由页面
   */
  private readonly defaultPath = ['/login']

  /**
   * 功能：记录第一个路由
   */
  private firstRoutePath: TCommon.OrNull<TMenuItem> = null

  /**
   * 功能：默认 meta 配置文件
   */
  private readonly defaultMetaConfig: TCommon.RouteConfig = {
    path: '',
    name: '',
    auth: true, // 默认取接口权限规则
    cache: true,
    dynamicParam: '',
    intercept: false,
    on: true,
    isChild: false,
    keepAlive: false,
  }

  /**
   * 功能： 路由实例
   */
  public router!: Router

  /**
   * 功能： 路由 routes 配置
   */
  private routes!: RouteRecordRaw[]

  /**
   * 功能：无权限路由，在默认页处理无权限路由逻辑，404，401，502等
   */
  private noAuthRouteQueue = new Map<string, TCommon.RouteConfig>()

  /**
   * 功能：存储用户登录后的个人信息
   */
  private userInfo: TCommon.OrNull<TUserInfo> = null

  /**
   * 功能：获取全部的路由配置文件中 layout 子页面
   */
  public getRoutes = () => {
    return this.router.options.routes.find((item) => item.name === 'layout')?.children
  }

  /**
   * 功能：默认路由页面，初始化进入的默认页面
   */
  private getDefaultIndex = () => {
    const { getState } = useStore()
    const { sActiveTab } = getState()
    return (
      sActiveTab || {
        path: this.firstRoutePath?.union?.path || '/home',
      }
    )
  }

  /**
   * 功能：处理页面信息检索数据
   */
  private retrieval = (menuType: keyof typeof ENUM.USER.MapMenuType, path: string, name: string, union?: TMenuItem['union']) => {
    const { MapMenuType } = ENUM.USER
    if (MapMenuType['目录'] === menuType) {
      union = {
        path,
        name,
        pinyin: toPinyin(name, { toneType: 'none' }),
      }
    } else {
      const { name: prevName = '', pinyin: prevPinyin = '' } = union || {}
      union = {
        path,
        name: [prevName, name].filter(Boolean).join('-'),
        pinyin: [toPinyin(name, { toneType: 'none' }), prevPinyin].filter(Boolean).join('-'),
      }
    }
    return union
  }

  /**
   * 功能：获取第一个路由元素
   */
  private getFirstRoutePath = (child: TMenuItem[]) => {
    const { MapMenuType } = ENUM.USER
    if (!this.firstRoutePath) {
      this.firstRoutePath = child[0]
    } else {
      const { menuType, child } = this.firstRoutePath
      if (child[0] && menuType == MapMenuType['目录']) {
        this.firstRoutePath = this.firstRoutePath.child[0]
      }
    }
  }

  /**
   * 功能：更新路由, 存在性能问题，后续优化，逻辑复杂，涉及到映射的逻辑
   * 将所有涉及到路由的数据，通过递归统一处理了
   * 1. authOpts：按钮操作权限
   * 2. authFields：列表数据权限
   * 3. 将接口返回的菜单信息挂载到路由 meta 的 menus 中
   * 4. 拼音和路由名称串联在了路由 meta 中 union 中，处理检索问题
   */
  private updateRoutes = (userInfo: TUserInfo | null) => {
    const { MapMenuType } = ENUM.USER
    const { router } = this

    const handleData = (
      child: Array<TMenuItem & { path?: string }>,
      ret: TCommon.Json<Omit<TMenuItem, 'child'>>,
      parentCode: string,
      union?: TMenuItem['union']
    ) => {
      this.getFirstRoutePath(child)
      if (child && child.length !== 0) {
        child.forEach((item) => {
          const { code, menuType, name } = item
          const path = parentCode + '/' + code
          if (MapMenuType['目录'] === menuType) {
            union = this.retrieval(menuType, path, name, union)
            handleData(item.child, ret, path, union)
          } else if (MapMenuType['菜单'] === menuType) {
            item.union = this.retrieval(menuType, path, name, union)
            ret[path] = omit(item, 'child')
            return handleData(item.child, ret, path, union)
          } else {
            if (MapMenuType['按钮权限'] === menuType) {
              let { authOpts } = ret[parentCode]
              ret[parentCode].authOpts = isArray(authOpts) ? [...authOpts, code] : [code]
            }
            if (MapMenuType['字段权限'] === menuType) {
              let { authFields } = ret[parentCode]
              ret[parentCode].authFields = isArray(authFields) ? [...authFields, code] : [code]
            }
          }
        })
      }
      return ret
    }

    return new Promise((resolve) => {
      const { dispatch } = useStore()
      const data = handleData(userInfo?.subMenu || [], {}, '', {}) // 接口数据
      const routes = router.getRoutes()
      //添加缓存页面
      routes?.forEach((item) => {
        if (item?.meta?.keepAlive) {
          dispatch({ type: 'common/addKeepAliveKeys', payload: item?.meta?.menus?.code || item.meta.componentName })
        }
      })
      Object.entries(data).forEach(([path, meta]) => {
        const curRoute = routes.find((route) => route.path === path)
        if (curRoute) {
          const title = meta.name || curRoute.meta.title || ''
          Object.assign(curRoute.meta, {
            menus: pick(meta, ['code', 'id', 'name', 'parentId', 'authOpts', 'authFields', 'status', 'menuType', 'union']),
            auth: true,
            title,
          })
        } else {
          // 接口有页面菜单，前端未建相应的页面，逻辑待实现
          this.noAuthRouteQueue.set(path, meta)
        }
      })
    })
  }

  /**
   * 功能：获取路由的 name
   */
  public getRouteName = (path: string) => path.replace(/^\/|\/$/g, '').replace(/\//g, '-')

  /**
   * 功能：重置路由 path, 慎用后期不好维护
   */
  private resetPath = (meta: RouteMeta, path: string) => {
    if (meta.path) {
      console.warn('it is not recommended to use the path mode.')
    }
    return this.formatPath(meta.path || path)
  }

  /**
   * 功能：检测并获取叶子和非叶子节点
   */
  private getPathLeaf(): TCommon.Json<string[]> {
    const { routePaths } = this
    const fn = (path: string) => routePaths.every((el) => path === el || !el.startsWith(path + '/'))
    return groupBy(routePaths, (path) => (fn(path) ? 'leafs' : 'nonLeafs'))
  }

  /**
   * 功能：设置动态路由逻辑
   * @param path：路径
   * @param dynamicParam：动态参数 /:id? 形式
   * @returns string
   */
  private setDynamicParam = (path: string, dynamicParam?: string) => {
    if (dynamicParam) {
      if (/^\/(.*):([^/]+)(\/|$)/.test(dynamicParam)) {
        path = path + dynamicParam
      } else {
        console.warn('dynamic route configuration is incorrect, it should comply with the format /:id? or /:id')
      }
    }
    return path
  }

  /**
   * 功能：预处理 路由配置文件
   */
  private getRawConfigFiles = (): TCommon.Json<TCommon.RouteConfig> => {
    const { configFiles, routePaths } = this
    return Object.entries(configFiles).reduce((prev, [dirname, meta]) => {
      const path = dirname.replace(/^.*views\/(.+?)\/route.config.ts$/, '$1')
      routePaths.push(path)
      return {
        ...prev,
        [path]: meta,
      }
    }, {})
  }

  /**
   * 功能：通过目录结构创建路由表
   *  1. 处理子路由逻辑, 仅支持一级子路由，即页面级子路由，
   *  2. 启用子路由逻辑较复请妥善使用，非必要不建议使用，父路由也需要有视图文件
   */
  private createRouteTableByFolder = (): RouteRecordRaw[] => {
    const { vueFiles, defaultMetaConfig, formatPath } = this
    const routes: RouteRecordRaw[] = []
    const views = Object.entries(vueFiles).reduce(
      (prev, [key, value]) => {
        key = key.replace(/^.*views\/(.+?)\.vue$/, '$1').replace(/\/\w+$/, '')
        if (key) {
          prev[key] = value
        }
        return prev
      },
      {} as Record<string, () => Promise<unknown>>
    )

    const rawConfig = this.getRawConfigFiles()

    const { leafs } = this.getPathLeaf()

    Object.entries(rawConfig).forEach(([path, meta]) => {
      const name = this.getRouteName(path)
      const component = views[path]

      path = this.resetPath(meta, path)

      // 处理动态路由
      path = this.setDynamicParam(path, meta.dynamicParam)

      const options = {
        path: '/' + path,
        name,
        component,
        meta: {
          ...omitBy(Object.assign({}, defaultMetaConfig, meta), (val) => val == ''),
        },
      }

      if (meta.isChild && leafs.includes(path)) {
        const [, parentPath, leaf] = path.match(/^(.+)(\/\w+)$/) || []
        const route = routes.find((item) => item.path == '/' + parentPath)

        if (route) {
          if (isArray(route.children)) {
            route.children.push(options)
          } else {
            route.children = [
              {
                ...options,
                path: '',
                alias: formatPath(leaf),
              },
            ]
          }
        } else {
          routes.push({
            path: '/' + parentPath,
            name: this.getRouteName(parentPath),
            component: views[parentPath],
            children: [
              {
                ...options,
                path: '',
                alias: formatPath(leaf),
              },
            ],
          })
        }
      } else {
        routes.push(options)
      }
    })

    return routes
  }

  /**
   * 功能：生成路由表
   */
  private generateRouteTable = () => {
    const { createRouteTableByFolder, defaultPath } = this
    const allRoutes = createRouteTableByFolder()
    const { common, children } = groupBy(allRoutes, (item) => (defaultPath.includes(item.path) ? 'common' : 'children'))
    const ret = [
      {
        path: '/',
        alias: ['/home'],
        name: 'layout',
        beforeEnter: async (to, from, next) => {
          const { dispatch } = useStore()
          if (!this.userInfo) {
            this.userInfo = (await dispatch({ type: 'login/userInfo' })) as TUserInfo
            this.updateRoutes(this.userInfo)
            return next(this.getDefaultIndex())
          } else {
            next()
          }
        },
        component: () => import('@/components/layout/index.vue'),
        children: [
          ...children,
          {
            path: '/:catchAll(.*)',
            meta: {
              title: this.defaultPage,
            },
            component: () => import('@/views/defaultPage/index.vue'),
          },
        ],
      },
      {
        path: '/:catchAll(.*)',
        redirect: () => this.defaultPage,
      },
      ...common,
    ] as RouteRecordRaw[]

    return ret
  }

  /**
   * 功能：路由拦截器
   */
  private routeHook = () => {
    const { router } = this
    const { getState, dispatch } = useStore()
    /**
     * 功能：路由进入前
     */
    router.beforeEach(async (to, from, next) => {
      const { sLoginInfo } = getState()
      if (!sLoginInfo?.access_token && to.path !== '/login') {
        next('/login')
      } else {
        next()
      }
    })

    /**
     * 功能：路由进入后
     */
    router.afterEach((to, from, next) => {
      if (to.meta.title === this.defaultPage) {
        const meta = this.noAuthRouteQueue.get(to.path)
        Object.assign(to.meta, {
          menus: meta,
          title: meta?.name || '404',
        })
      }
      if (to.path != '/login') {
        dispatch({ type: 'tab/add', payload: omit(to, 'matched', 'redirectedFrom') })
      }
      //从左侧菜单进入后keep-alive会被清空，路由加载完成以后判断该菜单是否需要加回keep-alive
      if (to.meta.keepAlive && to.meta.menus?.code) {
        dispatch({ type: 'common/addKeepAliveKeys', payload: to?.meta?.menus?.code })
      }

      document.title = to.meta.title || '新华书店'
    })
    /**
     * 功能：路由失败后的拦截
     */
    router.onError((error, to) => {
      console.error('路由跳转失败', error, to, import.meta.env.VITE_MODE)
      if (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes('Importing a module script failed')) {
        window.location.href = (to.fullPath as string) || ''
      }
    })
  }

  /**
   * 功能：获取路由实例
   */
  public get() {
    return this.router
  }

  /**
   * 功能：格式化 path
   */
  private formatPath = (path: string): string => func.removeChar(path, '/', 'start')

  /**
   * 功能： 扩展路由能力， 使用 $ 仅作为自定义扩展的方法名的标识，避免原生命名冲突等
   */
  private enhance = () => {
    const { router, formatPath, getRouteName } = this

    /**
     * 功能：在当前路由后面追加路径，query/params 并跳转
     */
    router.$append = (to) => {
      const route = router.currentRoute.value
      if (isString(to)) {
        to = route.path + formatPath(to)
      } else if ('path' in to && to.path) {
        to.path = route.path + formatPath(to.path)
      } else if ('name' in to) {
        to.name = [getRouteName(route.path), to.name].join('-')
      }
      return router.push(to)
    }

    /**
     * 功能：回退并刷新页面，后期根据场景结合页面缓存，采用分解路径的方式决定缓存页面是否需要更新状态。
     * 需要严格管控路径规则，后续更新迭代处理
     */
    router.$back = () => {
      const { dispatch } = useStore()
      const route = router.currentRoute.value
      dispatch({
        type: 'tab/remove',
        payload: route.fullPath,
      })
      return router.back()
    }
  }

  /**
   * 功能：启动路由
   */
  public run() {
    const routes = this.generateRouteTable()
    const router = createRouter({
      history: createWebHistory(),
      routes,
    })
    this.routes = routes
    this.router = router
    this.routeHook()
    this.enhance()
    return router
  }
}

export const superRouter = new SuperRouter()
